import { useEffect, useState } from 'react'
import { ExperienceCanvas } from './scene/ExperienceCanvas'
import { PersonComposite } from './ui/PersonComposite'
import { TimelineBar } from './ui/TimelineBar'
import { PrivacyBanner } from './ui/PrivacyBanner'
import { Onboarding } from './ui/Onboarding'
import { Hud } from './ui/Hud'
import { LoadingScreen } from './ui/LoadingScreen'
import { useCameraStream } from './hooks/useCameraStream'
import { useMediaPipeHands } from './hooks/useMediaPipeHands'
import { useKeyboardMouse } from './hooks/useKeyboardMouse'
import { useExperienceStore } from './store/experienceStore'

export default function App() {
  const privacyAccepted = useExperienceStore((s) => s.privacyAccepted)
  const showPersonPreview = useExperienceStore((s) => s.showPersonPreview)
  const cameraGranted = useExperienceStore((s) => s.cameraGranted)
  const [wantsCamera, setWantsCamera] = useState(false)

  // Camera only after privacy + user chose camera path
  const cameraEnabled =
    privacyAccepted && wantsCamera && cameraGranted !== false

  const { videoRef, status } = useCameraStream({
    enabled: cameraEnabled,
    stopOnHidden: true,
  })

  const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null)
  useEffect(() => {
    setVideoEl(videoRef.current)
  }, [videoRef, status, cameraEnabled])

  useMediaPipeHands(videoEl, cameraEnabled && status === 'active')
  useKeyboardMouse()

  useEffect(() => {
    if (status === 'active') {
      useExperienceStore.getState().setCameraGranted(true)
    } else if (status === 'denied' || status === 'unavailable') {
      useExperienceStore.getState().setCameraGranted(false)
      useExperienceStore.getState().setInputMode('mouse')
      useExperienceStore
        .getState()
        .setBannerMessage('无法使用摄像头，已切换为键鼠控制时间')
    } else if (status === 'ended') {
      useExperienceStore.getState().setBannerMessage('摄像头已断开，可刷新后重试')
      useExperienceStore.getState().setInputMode('mouse')
    }
  }, [status])

  useEffect(() => {
    // progressive load progress simulation until textures mark ready
    const t = window.setInterval(() => {
      const s = useExperienceStore.getState()
      if (s.loadProgress < 0.95) {
        s.setLoadProgress(Math.min(0.95, s.loadProgress + 0.05))
      }
      const loaded = Object.keys(s.loadedEras).length
      if (loaded >= 2) s.setLoadProgress(1)
    }, 200)
    return () => clearInterval(t)
  }, [])

  // ?debug=1
  useEffect(() => {
    const q = new URLSearchParams(window.location.search)
    if (q.get('demo') === '1') {
      useExperienceStore.getState().setPrivacyAccepted(true)
      useExperienceStore.getState().setCameraGranted(false)
      useExperienceStore.getState().setInputMode('mouse')
      useExperienceStore.getState().setOnboardingStep(null)
      useExperienceStore.getState().setReady(true)
      useExperienceStore.getState().setLoadProgress(1)
      // animate time reverse then forward for demo polish
      let t = 0
      const id = window.setInterval(() => {
        t += 1
        const store = useExperienceStore.getState()
        if (t < 40) store.setTimeAbsolute(Math.max(0, 0.92 - t * 0.02))
        else if (t < 80) store.setTimeAbsolute(Math.min(1, (t - 40) * 0.02))
        else window.clearInterval(id)
      }, 50)
    }
    if (q.get('debug') === '1') {
      useExperienceStore.getState().setShowDebug(true)
    }
    if (q.get('quality') === 'low' || q.get('quality') === 'medium' || q.get('quality') === 'high') {
      useExperienceStore.getState().setQuality(q.get('quality') as 'low' | 'medium' | 'high')
      useExperienceStore.getState().setQualityLocked(true)
    }
  }, [])

  return (
    <div className="app-root" data-testid="lucy-app">
      <ExperienceCanvas />

      <PersonComposite
        videoRef={videoRef}
        visible={
          showPersonPreview &&
          cameraEnabled &&
          status === 'active'
        }
      />

      <Hud />
      <TimelineBar />
      <LoadingScreen />

      <PrivacyBanner
        onAccept={() => {
          // If camera was not explicitly denied via "键鼠", enable camera
          const granted = useExperienceStore.getState().cameraGranted
          if (granted !== false) {
            setWantsCamera(true)
            useExperienceStore.getState().setCameraGranted(true)
          }
          useExperienceStore.getState().setOnboardingStep(0)
        }}
      />
      {privacyAccepted && <Onboarding />}
    </div>
  )
}
